import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { AuthContext } from "../context/Auth/AuthContext";
import { getCompanyExpiryStatus } from "../services/companyExpiryService";
import BackdropLoading from "./BackdropLoading";
import Financeiro from "../pages/Financeiro";

const CompanyBlockingMiddleware = ({ children }) => {
	const { isAuth, user } = useContext(AuthContext);
	const [isBlocked, setIsBlocked] = useState(false);
	const [isChecking, setIsChecking] = useState(true);
	const location = useLocation();
	const history = useHistory();
	const gracefulRedirectDone = useRef(false);

	// #4258: pathname e history entravam nas deps do callback abaixo, e o
	// callback é dep do efeito de verificação. Resultado: QUALQUER navegação
	// (abrir um ticket, por exemplo) refazia a verificação com loading, e o
	// middleware trocava os children pelo BackdropLoading — desmontando a
	// página inteira e o estado local dela. Em refs, o valor continua atual
	// sem recriar o callback.
	const locationRef = useRef(location);
	const historyRef = useRef(history);
	locationRef.current = location;
	historyRef.current = history;

	// A primeira verificação é a única que pode esconder a tela; as seguintes
	// (periódicas) correm em segundo plano — trocar os children por um loading
	// depois da tela montada é o que derrubava o clique do usuário.
	const primeiraVerificacaoFeita = useRef(false);

	const checkCompanyStatus = useCallback(async (showLoading = false) => {
		if (!isAuth || !user?.companyId) {
			setIsBlocked(false);
			setIsChecking(false);
			primeiraVerificacaoFeita.current = true;
			return;
		}

		if (showLoading && !primeiraVerificacaoFeita.current) {
			setIsChecking(true);
		}

		try {
			const { data } = await getCompanyExpiryStatus();
			console.log("🔒 Middleware - Status da empresa:", data.status, "(verificação automática)");

			if (data.status === "expired_blocked") {
				setIsBlocked(true);
				console.log("🚨 MIDDLEWARE - Empresa bloqueada! Renderizando financeiro");
			} else {
				setIsBlocked(false);
				console.log("✅ MIDDLEWARE - Empresa livre para navegar");

				if (
					data.status === "expired_with_grace" &&
					!gracefulRedirectDone.current &&
					locationRef.current.pathname !== "/financeiro"
				) {
					gracefulRedirectDone.current = true;
					console.log("⚠️ MIDDLEWARE - Grace period ativo, redirecionando para /financeiro");
					historyRef.current.push("/financeiro");
				}
			}
		} catch (error) {
			console.error("Erro ao verificar status da empresa:", error);
			setIsBlocked(false);
		}

		if (showLoading) {
			setIsChecking(false);
		}
		primeiraVerificacaoFeita.current = true;
	}, [isAuth, user?.companyId]);

	// Verificação inicial
	useEffect(() => {
		checkCompanyStatus(true);
	}, [checkCompanyStatus]);

	// Verificação periódica inteligente - frequência baseada no status
	useEffect(() => {
		if (!isAuth || !user?.companyId) {
			return;
		}

		// Frequência baseada no status:
		// - Bloqueado: 30s (para detectar rapidamente quando for desbloqueado)
		// - Liberado: 5 minutos (menos requests desnecessários)
		const intervalTime = isBlocked ? 30000 : 300000; // 30s vs 5min
		const statusText = isBlocked ? "bloqueada (30s)" : "liberada (5min)";

		console.log(`🔄 MIDDLEWARE - Verificação periódica para empresa ${statusText}`);

		const interval = setInterval(() => {
			console.log(`⏰ MIDDLEWARE - Verificação automática (empresa ${statusText})`);
			checkCompanyStatus(false); // Não mostra loading nas verificações automáticas
		}, intervalTime);

		return () => {
			console.log("🛑 MIDDLEWARE - Parando verificação periódica");
			clearInterval(interval);
		};
	}, [isAuth, user?.companyId, checkCompanyStatus, isBlocked]); // Adiciona isBlocked nas dependências

	// Mostra loading apenas na verificação inicial
	if (isChecking) {
		return <BackdropLoading />;
	}

	// Se estiver bloqueado, sempre renderiza a página do financeiro
	if (isBlocked) {
		console.log("🛑 MIDDLEWARE - Bloqueado! Renderizando Financeiro em vez de:", location.pathname);
		return <Financeiro />;
	}

	// Caso contrário, renderiza o conteúdo normal
	return children;
};

export default CompanyBlockingMiddleware;